import { DOCUMENT } from '@angular/common';
import { afterNextRender, Component, inject, signal } from '@angular/core';
import { RouterOutlet } from '@angular/router';

import { environment } from '../environments/environment';

import { EnvironmentBannerComponent } from './components/environment-banner/environment-banner.component';
import { ContextualHelpDrawerComponent } from './components/shared/contextual-help-drawer/contextual-help-drawer.component';
import { BrowserApiService } from './services/browser-api.service';
import { SeoService } from './services/seo.service';
import { SessionSyncService } from './services/session-sync.service';
import { ThemeService } from './services/theme.service';
import { TokenExpirationService } from './services/token-expiration.service';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, EnvironmentBannerComponent, ContextualHelpDrawerComponent],
  templateUrl: './app.html',
  styleUrl: './app.scss',
})
export class App {
  protected readonly title = signal('darena');
  protected readonly env = environment;

  private readonly themeService = inject(ThemeService);
  private readonly tokenExpirationService = inject(TokenExpirationService);
  private readonly seoService = inject(SeoService);
  private readonly browserApi = inject(BrowserApiService);
  private readonly sessionSync = inject(SessionSyncService);
  private readonly document = inject(DOCUMENT);

  constructor() {
    afterNextRender(() => {
      this.sessionSync.initSync();
      this.observeEnvironmentBannerHeight();
    });

    // Only show verbose diagnostic logs if VERBOSITY is set to DEBUG
    if (environment.verbosity === 'DEBUG' && this.browserApi.isBrowser) {
      const runtimeOrigin = this.browserApi.window?.location.origin || '';
      const configuredAppUrl = environment.appUrl.replace(/\/$/, '');
      const configSource =
        this.browserApi.window && 'env' in this.browserApi.window ? 'config.js' : 'environment.ts';

      console.warn(`🔷 Darena v${environment.version} initialized (${configSource})`);
      console.warn('Runtime Environment:', {
        Environment: environment.environment,
        Verbosity: environment.verbosity,
        'API URL': environment.apiUrl,
        'APP URL': environment.appUrl,
        'Landing URL': environment.landingUrl,
        'Runtime Origin': runtimeOrigin,
        'Detection Method': configSource,
      });

      if (runtimeOrigin && configuredAppUrl && runtimeOrigin !== configuredAppUrl) {
        console.warn(
          `⚠️ Configuration Mismatch: The app is running at "${runtimeOrigin}" ` +
            `but APP_URL is configured as "${configuredAppUrl}".`,
        );
      }
    }
  }

  // Exposes the environment banner's actual rendered height (0 when hidden, e.g. in
  // production) as a CSS variable, so the fixed nav header can sit right below it
  // instead of overlapping it.
  private observeEnvironmentBannerHeight(): void {
    const root = this.document.documentElement;
    const banner = this.document.getElementById('env-ribbon');

    if (!banner) {
      root.style.setProperty('--env-banner-height', '0px');
      return;
    }

    const updateHeight = (): void => {
      root.style.setProperty('--env-banner-height', `${banner.getBoundingClientRect().height}px`);
    };

    updateHeight();
    new ResizeObserver(updateHeight).observe(banner);
  }
}
